import { useMemo, useState } from "react";
import MaskedLines from "../components/MaskedLines";
import Reveal from "../components/Reveal";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

const inr = (n) => `₹${Math.round(n).toLocaleString("en-IN")}`;
const pct = (n) => `${n.toFixed(2)}%`;

export default function Services() {
  // NRI Calculator
  const [nri, setNri] = useState({ value: 20000000, rent: 65000, pkg: "full" });
  const mgmtFeePct = nri.pkg === "full" ? 0.10 : 0.05;
  const gross = nri.rent * 12;
  const fee = gross * mgmtFeePct;
  const net = gross - fee;
  const netYield = (net / nri.value) * 100;

  // Construction
  const TIERS = { classic: 1800, premium: 2400, luxury: 3200 };
  const [con, setCon] = useState({ area: 3200, tier: "premium" });
  const civil = con.area * TIERS[con.tier] * 0.62;
  const interior = con.area * TIERS[con.tier] * 0.28;
  const pm = con.area * TIERS[con.tier] * 0.10;
  const conTotal = civil + interior + pm;

  // JV
  const [jv, setJv] = useState({ acres: 4.2, gdv: 850000000 });
  const landowner = jv.gdv * 0.6;
  const bb = jv.gdv * 0.4;

  return (
    <div className="pt-[112px]">
      {/* Header */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="eyebrow mb-6 flex items-center gap-3"><span className="accent-dot" /> Services Stack · Interactive Calculators</div>
        <h1 className="display-1 max-w-5xl">
          <MaskedLines lines={["Underwrite the return", "before you sign the paper."]} />
        </h1>
        <p className="mt-10 max-w-2xl text-[15px] leading-[1.8] text-[#EDE6D3]/75">
          Three institutional-grade tools we use in-house — rebuilt in the browser. Run your own numbers before a single call.
        </p>
      </section>

      <section className="max-w-[1440px] mx-auto px-6 md:px-10 pt-24">
        <Tabs defaultValue="nri" className="w-full" data-testid="calculator-tabs">
          <TabsList className="w-full flex bg-transparent border-b border-[#EDE6D3]/10 rounded-none p-0 h-auto justify-start gap-0">
            <TabsTrigger value="nri" data-testid="tab-nri" className="rounded-none py-5 px-6 text-[11px] tracking-[2px] uppercase data-[state=active]:bg-[#EDE6D3] data-[state=active]:text-[#0A2342] data-[state=active]:shadow-none">
              01 · NRI ROI Estimator
            </TabsTrigger>
            <TabsTrigger value="build" data-testid="tab-build" className="rounded-none py-5 px-6 text-[11px] tracking-[2px] uppercase data-[state=active]:bg-[#EDE6D3] data-[state=active]:text-[#0A2342] data-[state=active]:shadow-none">
              02 · Turnkey Construction
            </TabsTrigger>
            <TabsTrigger value="jv" data-testid="tab-jv" className="rounded-none py-5 px-6 text-[11px] tracking-[2px] uppercase data-[state=active]:bg-[#EDE6D3] data-[state=active]:text-[#0A2342] data-[state=active]:shadow-none">
              03 · JV Revenue Split
            </TabsTrigger>
          </TabsList>

          <TabsContent value="nri" className="pt-14">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
              <div className="md:col-span-5">
                <div className="chapter-num mb-3">§ 01</div>
                <h2 className="font-serif text-[38px] md:text-[46px] leading-tight tracking-tight">NRI Rental & Property Management ROI.</h2>
                <p className="mt-6 text-[14px] leading-[1.8] text-[#EDE6D3]/70">Model the net yield you'd retain after our management fee — with realistic vacancy and maintenance assumptions baked in.</p>
                <div className="mt-10 space-y-8">
                  <Field label="Property Value (₹)"><input type="number" className="bb-input" value={nri.value} onChange={(e) => setNri({ ...nri, value: +e.target.value })} data-testid="nri-value" /></Field>
                  <Field label="Monthly Rent Target (₹)"><input type="number" className="bb-input" value={nri.rent} onChange={(e) => setNri({ ...nri, rent: +e.target.value })} data-testid="nri-rent" /></Field>
                  <div>
                    <div className="bb-label mb-3">Service Package</div>
                    <div className="grid grid-cols-2 gap-3">
                      {["basic", "full"].map((p) => (
                        <button key={p} onClick={() => setNri({ ...nri, pkg: p })} data-testid={`nri-pkg-${p}`}
                          className={`text-[11px] tracking-[2px] uppercase py-3 border ${nri.pkg === p ? "bg-[#EDE6D3] text-[#0A2342] border-[#EDE6D3]" : "border-[#EDE6D3]/20 hover:border-[#D4AF37]"}`}>
                          {p === "basic" ? "Basic · 5% fee" : "Full Mgmt · 10% fee"}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="md:col-span-7">
                <div className="card-sand p-8 md:p-12">
                  <div className="eyebrow">Estimated Output</div>
                  <div className="mt-6 grid grid-cols-2 gap-8">
                    <BigStat label="Annual Gross Rent" value={inr(gross)} />
                    <BigStat label="Net Yield" value={pct(netYield)} accent />
                    <BigStat label="Management Fee" value={inr(fee)} />
                    <BigStat label="Net Annual Income" value={inr(net)} />
                  </div>
                  <div className="mt-10 hairline" />
                  <div className="mt-6 text-[12px] leading-[1.8] text-[#EDE6D3]/60">
                    Assumes 100% occupancy; deduct ~5% for vacancy buffer. Excludes property tax and society charges. Full Management includes tenant onboarding, rent collection, monthly inspections and repatriation setup.
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="build" className="pt-14">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
              <div className="md:col-span-5">
                <div className="chapter-num mb-3">§ 02</div>
                <h2 className="font-serif text-[38px] md:text-[46px] leading-tight tracking-tight">Turnkey Civil + Interior Cost.</h2>
                <p className="mt-6 text-[14px] leading-[1.8] text-[#EDE6D3]/70">All-inclusive per-sqft pricing with a fixed-price contract, SLA quality assurance and a five-year structural warranty.</p>
                <div className="mt-10 space-y-8">
                  <Field label="Total Built-Up Area (sq. ft.)"><input type="number" className="bb-input" value={con.area} onChange={(e) => setCon({ ...con, area: +e.target.value })} data-testid="build-area" /></Field>
                  <div>
                    <div className="bb-label mb-3">Quality Tier</div>
                    <div className="grid grid-cols-3 gap-3">
                      {Object.entries(TIERS).map(([k, v]) => (
                        <button key={k} onClick={() => setCon({ ...con, tier: k })} data-testid={`build-tier-${k}`}
                          className={`text-[11px] tracking-[2px] uppercase py-3 border ${con.tier === k ? "bg-[#EDE6D3] text-[#0A2342] border-[#EDE6D3]" : "border-[#EDE6D3]/20 hover:border-[#D4AF37]"}`}>
                          {k}<br /><span className="text-[10px] opacity-70">₹{v}/sqft</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="md:col-span-7">
                <div className="card-sand p-8 md:p-12">
                  <div className="eyebrow">Cost Breakdown</div>
                  <div className="mt-6 space-y-5">
                    <Row label="Civil & Structure (62%)" value={inr(civil)} />
                    <Row label="Interiors & Finishes (28%)" value={inr(interior)} />
                    <Row label="PM, Design & Compliance (10%)" value={inr(pm)} />
                    <div className="hairline my-2" />
                    <Row label="Estimated Total Project Cost" value={inr(conTotal)} bold />
                  </div>
                  <div className="mt-10 text-[12px] leading-[1.8] text-[#EDE6D3]/60">Includes GST, structural design fees, MEP, and finishing labour. Excludes land, statutory approvals and premium imported specifications.</div>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="jv" className="pt-14">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
              <div className="md:col-span-5">
                <div className="chapter-num mb-3">§ 03</div>
                <h2 className="font-serif text-[38px] md:text-[46px] leading-tight tracking-tight">Land JV Revenue Split.</h2>
                <p className="mt-6 text-[14px] leading-[1.8] text-[#EDE6D3]/70">Default DM split assumes 60/40 in favour of the landowner. Adjust GDV assumptions to model your realized share.</p>
                <div className="mt-10 space-y-8">
                  <Field label="Land Area (Acres)"><input type="number" step="0.1" className="bb-input" value={jv.acres} onChange={(e) => setJv({ ...jv, acres: +e.target.value })} data-testid="jv-acres" /></Field>
                  <Field label="Expected Gross Development Value (₹)"><input type="number" className="bb-input" value={jv.gdv} onChange={(e) => setJv({ ...jv, gdv: +e.target.value })} data-testid="jv-gdv" /></Field>
                </div>
              </div>
              <div className="md:col-span-7">
                <div className="card-sand p-8 md:p-12">
                  <div className="eyebrow">Revenue Distribution</div>
                  <div className="mt-8 grid grid-cols-2 gap-6">
                    <BigStat label="Landowner Share (60%)" value={inr(landowner)} accent />
                    <BigStat label="BBE DM (40%)" value={inr(bb)} />
                  </div>
                  <div className="mt-10">
                    <div className="w-full h-3 bg-[#0A2342] border border-[#EDE6D3]/15 overflow-hidden" data-testid="jv-split-bar">
                      <div className="h-full bg-[#D4AF37] flex" style={{ width: "60%" }} />
                    </div>
                    <div className="mt-3 flex justify-between text-[10px] tracking-[2px] uppercase text-[#EDE6D3]/60">
                      <span>Landowner · 60%</span><span>BBE · 40%</span>
                    </div>
                  </div>
                  <div className="mt-10 hairline" />
                  <div className="mt-6 text-[12px] leading-[1.8] text-[#EDE6D3]/60">Model assumes standard DM structure with Build Brigade Enterprises underwriting design, approvals, construction and sales. Alternate 65/35 and 55/45 splits available for premium locations.</div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </section>

      {/* Service depth blocks */}
      <ServiceDeepDive />

      {/* Construction materials */}
      <ConstructionMaterials />
    </div>
  );
}

function Field({ label, children }) {
  return <div><div className="bb-label mb-2">{label}</div>{children}</div>;
}
function BigStat({ label, value, accent }) {
  return (
    <div>
      <div className="eyebrow text-[#EDE6D3]/50">{label}</div>
      <div className={`mt-3 font-serif text-[36px] md:text-[44px] leading-none tracking-tight ${accent ? "text-[#D4AF37]" : ""}`}>{value}</div>
    </div>
  );
}
function Row({ label, value, bold }) {
  return (
    <div className={`flex justify-between items-baseline ${bold ? "text-[#EDE6D3]" : "text-[#EDE6D3]/80"}`}>
      <span className={`text-[13px] ${bold ? "font-serif text-[15px]" : ""}`}>{label}</span>
      <span className={`${bold ? "font-serif text-[32px]" : "font-serif text-[20px]"}`}>{value}</span>
    </div>
  );
}

function ServiceDeepDive() {
  const items = [
    { n: "01", t: "Land Aggregation & Plotted Layouts", d: "Discreet parcel aggregation in high-growth corridors with title indemnity underwriting." },
    { n: "02", t: "Turnkey Civil & Interior", d: "Fixed-price contracts, SLA-backed timelines, 5-year structural warranty." },
    { n: "03", t: "NRI Rental & Property Management", d: "Tenant vetting, rent repatriation, statutory filings, quarterly inspections." },
    { n: "04", t: "Commercial Leasing & Sale-Leaseback", d: "Pre-leased Grade-A floors with 9%+ triple-net yield." },
    { n: "05", t: "Distressed Acquisitions", d: "SARFAESI-cell access, pre-NCLT bids, 30-45% below market entries." },
    { n: "06", t: "JV Development Management", d: "60/40 default, single-window design, approvals and sales." },
  ];
  return (
    <section className="max-w-[1440px] mx-auto px-6 md:px-10 pt-40">
      <div className="eyebrow mb-6">Verticals · Detail</div>
      <h2 className="font-serif text-[44px] md:text-[64px] leading-[0.95] tracking-tight max-w-3xl">Six mandates, one balance sheet.</h2>
      <div className="mt-16 border-t border-[#EDE6D3]/20">
        {items.map((s, i) => (
          <Reveal key={s.n} delay={i * 0.05}>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 py-10 border-b border-[#EDE6D3]/15 items-start group">
              <div className="md:col-span-1 chapter-num">— {s.n}</div>
              <div className="md:col-span-4 font-serif text-[26px] md:text-[32px] leading-tight">{s.t}</div>
              <div className="md:col-span-6 md:col-start-7 text-[14px] leading-[1.9] text-[#EDE6D3]/70">{s.d}</div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}


const MATERIAL_STAGES = [
  {
    n: "01",
    stage: "Substructure & Foundation",
    items: [
      { label: "Cement", spec: "UltraTech / ACC · OPC 53-grade & PPC" },
      { label: "Reinforcement Steel", spec: "TATA Tiscon / JSW Neosteel · Fe 500D TMT" },
      { label: "Aggregate", spec: "20mm & 12mm blue-metal · washed river sand / M-sand IS-383" },
      { label: "Waterproofing", spec: "Fosroc / Dr. Fixit — crystalline slurry + bentonite membrane" },
      { label: "Anti-Termite", spec: "Bayer Premise pre-construction soil treatment · 10-yr warranty" },
    ],
  },
  {
    n: "02",
    stage: "Superstructure & Masonry",
    items: [
      { label: "Concrete", spec: "M25 / M30 site-batched with cube-tested slump" },
      { label: "Blocks", spec: "Solid concrete 200mm · AAC blocks (Siporex / Magicrete) for internal walls" },
      { label: "Formwork", spec: "MIVAN aluminium / marine-grade ply · reused ≤ 6 cycles" },
      { label: "MEP Sleeves", spec: "GI conduits pre-laid before pour · isometric tagged" },
      { label: "Curing", spec: "14-day water curing + curing-compound seal" },
    ],
  },
  {
    n: "03",
    stage: "Plumbing, Sanitary & Electrical",
    items: [
      { label: "Water Lines", spec: "Astral / Ashirvad CPVC hot-cold · pressure-tested 1.5x working" },
      { label: "Drainage", spec: "Supreme / Finolex SWR uPVC · leak-tested to slab" },
      { label: "Sanitaryware", spec: "Kohler / Grohe / Jaquar Artize · premium tier" },
      { label: "Wiring", spec: "Polycab / Havells FR-LSH copper · Legrand / Schneider switches" },
      { label: "Distribution", spec: "L&T / ABB MCB DBs · single-earth pit per phase + lightning arrestor" },
      { label: "Smart Home", spec: "Optional — KNX bus or Wi-Fi mesh (Aqara / Google Home)" },
    ],
  },
  {
    n: "04",
    stage: "Waterproofing, Plaster & Screed",
    items: [
      { label: "Terrace WP", spec: "Fosroc Nitoproof 600 + PU top-coat · 10-yr sealed warranty" },
      { label: "Toilet WP", spec: "Dr. Fixit Roofseal Flex 2-coat + APP membrane" },
      { label: "External Plaster", spec: "20mm double-coat · sponge finish · CM 1:5" },
      { label: "Internal Plaster", spec: "12mm single-coat · gypsum skim (Saint-Gobain Gyproc) for Premium/Luxury" },
    ],
  },
  {
    n: "05",
    stage: "Flooring & Wall Finishes",
    items: [
      { label: "Living / Bedrooms", spec: "Kajaria / Somany 800×1600 vitrified · or Italian marble (Luxury)" },
      { label: "Wet Areas", spec: "Anti-skid ceramic · Kajaria Prima Plus / Nitco premium" },
      { label: "Paint — Internal", spec: "Asian Paints Royale Aspira / Berger Silk Glamor · 4 coats" },
      { label: "Paint — External", spec: "Asian Paints Apex Ultima / Nerolac Excel Total · anti-carbonation" },
      { label: "Ceilings", spec: "Gypsum + POP cove · Luxury tier: linear wooden slat inserts" },
    ],
  },
  {
    n: "06",
    stage: "Doors, Windows & Joinery",
    items: [
      { label: "Main Door", spec: "Solid Burma teak / engineered flush with veneer · SS hinges + digital lock" },
      { label: "Internal Doors", spec: "Century Ply / Greenply flush · laminate or veneer finish" },
      { label: "Windows", spec: "Fenesta / Aluplast uPVC / thermal-break aluminium · double-glazed SGU (Luxury)" },
      { label: "Kitchen", spec: "Hettich soft-close hardware · Merino / Greenlam laminate shutters · quartz counter" },
      { label: "Wardrobes", spec: "Hafele / Ebco fittings · pre-laminated / veneer / lacquer finishes" },
    ],
  },
  {
    n: "07",
    stage: "Kitchen, Appliances & Fixtures",
    items: [
      { label: "Counter", spec: "Kalinga stone / Caesarstone quartz · 20mm with mitred waterfall (Luxury)" },
      { label: "Sink & Taps", spec: "Franke / Kohler under-mount · pull-out mixer" },
      { label: "Chimney & Hob", spec: "Faber / Elica · 90cm auto-clean · 4-burner brass burners" },
      { label: "Water Purifier", spec: "AquaGuard / Kent Grand+ · UV+RO+TDS controller" },
    ],
  },
  {
    n: "08",
    stage: "Handover, Testing & Warranty",
    items: [
      { label: "Snag Rounds", spec: "3 internal + 1 client walk-through before handover" },
      { label: "Testing", spec: "Ponding, hydro, cube, plumb, insulation-resistance — all documented" },
      { label: "Warranty", spec: "5-yr structural · 2-yr waterproofing · 1-yr defects liability" },
      { label: "Handover Kit", spec: "As-built drawings, MEP layouts, warranty cards, OC / CC copies" },
    ],
  },
];

function ConstructionMaterials() {
  return (
    <section className="max-w-[1440px] mx-auto px-6 md:px-10 pt-40" data-testid="materials-section">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-16">
        <div className="md:col-span-6">
          <div className="chapter-num">CHAPTER · Materials</div>
          <h2 className="mt-4 font-serif text-[44px] md:text-[64px] leading-[0.95] tracking-tight">Every bag, every bolt — declared.</h2>
        </div>
        <div className="md:col-span-5 md:col-start-8">
          <p className="text-[14px] leading-[1.9] text-[#EDE6D3]/70">
            Our turnkey contract publishes the exact brand and specification of every material — from foundation to handover. No downgrades, no substitutions, and every batch is verified against the delivery challan.
          </p>
        </div>
      </div>

      <div className="border-t border-[#EDE6D3]/20">
        {MATERIAL_STAGES.map((s, i) => (
          <Reveal key={s.n} delay={i * 0.03}>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 py-10 border-b border-[#EDE6D3]/15 items-start" data-testid={`material-stage-${s.n}`}>
              <div className="md:col-span-3">
                <div className="chapter-num">— {s.n}</div>
                <h3 className="mt-3 font-serif text-[26px] md:text-[30px] leading-tight tracking-tight">{s.stage}</h3>
              </div>
              <div className="md:col-span-9 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5">
                {s.items.map((it) => (
                  <div key={it.label} className="border-l border-[#D4AF37]/40 pl-4">
                    <div className="text-[11px] tracking-[2px] uppercase text-[#D4AF37]">{it.label}</div>
                    <div className="mt-1.5 text-[13px] leading-[1.55] text-[#EDE6D3]/85">{it.spec}</div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      <div className="mt-10 flex flex-wrap gap-4 items-center text-[12px] tracking-[1.5px] uppercase text-[#EDE6D3]/60">
        <span className="w-2 h-2 rounded-full bg-[#D4AF37]" />
        <span>Classic tier follows the base spec above. Premium and Luxury tiers upgrade finishes, fittings and imported specifications — full BOQ shared on request.</span>
      </div>
    </section>
  );
}
